import React from 'react';
import {
    Box, Typography, Paper, Grid, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useReport } from '../../contexts/ReportContext';
import { lifeCarePlanCategoryConfig } from '../LifeCarePlan/lifeCarePlanCategoryConfig';

// --- Helper functions and definitions for Review Display (Copied from Wizard.js) ---


// Helper function to format data for display
const formatValue = (value, fieldKey = '') => { // Added fieldKey for context
    if (typeof value === 'number' || tableCurrencyFields.includes(fieldKey) || simpleCurrencyKeys.includes(fieldKey)) {
        // Existing rate formatting
        if (Math.abs(value) < 1 && Math.abs(value) > 0 && (fieldKey === 'discountRate' || fieldKey === 'growthRate' || fieldKey === 'netDiscountRate')) {
            return `${(value * 100).toFixed(2)}%`;
        }
        // Existing currency formatting for simpleCurrencyKeys and tableCurrencyFields
        if (simpleCurrencyKeys.includes(fieldKey) || tableCurrencyFields.includes(fieldKey)) {
             if (!value.toString().includes('.') || (Math.abs(value) >= 1 || value === 0) ) { // Integer or larger decimal
                return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
             }
        }
        // Default number formatting (including decimals not caught above)
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    return value || 'N/A';
};

// List of keys that represent currency values in simple fields
const simpleCurrencyKeys = ['regionalAdjustmentFactor']; // Add others if needed

// Helper to render simple key-value pairs
const renderSimpleFields = (data, title) => (
    <Box sx={{ mb: 3 }}> {/* Increased margin bottom */}
        <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>{title}</Typography> {/* Added margin bottom to title */}
        <Grid container spacing={2}> {/* Increased spacing between grid items */}
            {Object.entries(data).map(([key, value]) => {
                // Pass the key to formatValue for context-specific formatting
                const displayValue = formatValue(value, key);
                return (
                    <Grid item xs={12} sm={6} md={4} key={key}> {/* Adjusted for 1, 2, or 3 items per row based on screen size */}
                        <Typography variant="body1" component="span" sx={{ fontWeight: 'bold' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}: </Typography>
                        <Typography variant="body1" component="span">{displayValue}</Typography> {/* Changed variant to body1 */}
                    </Grid>
                );
            })}
        </Grid>
    </Box>
);

// Helper to render a single field's label and formatted value
const renderFieldWithValue = (label, value, fieldKey = '') => ( // Added fieldKey
    <>
        <Typography variant="body1" component="span" sx={{ fontWeight: 'bold' }}>
            {label.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:{' '}
        </Typography>
        <Typography variant="body1" component="span">
            {formatValue(value, fieldKey)} {/* Pass fieldKey to formatValue */}
        </Typography>
    </>
);

// List of field names that represent currency values in tables
const tableCurrencyFields = [
    'annualIncome', 'presentValue', 'futureValue', 'baseHourlyRate', 'amount',
    'presentHourlyRate', 'futureHourlyRate', 'costPerTreatment', 'annualCost', 'costPerUnit',
    'annualGrossIncome', 'annualNetLoss'
];

// Helper to render table data
const renderTable = (data, title, columns) => (
     <Box sx={{ mb: 3 }}> {/* Increased margin bottom */}
        <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>{title}</Typography> {/* Added margin bottom to title */}
        <TableContainer component={Paper} elevation={1}> {/* Added slight elevation to tables */}
            <Table>{/* Removed size="small" */}
                <TableHead>
                    <TableRow>
                        {(columns?? []).map(col => 
                        <TableCell 
                            key={col.field} 
                            align={col.align ?? 'left'}
                            sx={{ fontWeight: 'bold' }}>{col.headerName}</TableCell>)} {/* Made header bold */}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data?.map((item, index) => (
                        <TableRow key={item.id || index}>
                             {columns.map(col => {
                                const rawValue = item[col.field];
                                const displayValue = formatValue(rawValue, col.field);
                                return <TableCell 
                                    key={col.field}
                                    align={col.align ?? 'left'}>
                                    {displayValue}
                                </TableCell>;
                             })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const householdServicesColumns = [
    { field: 'start', headerName: 'Start Date' },
    { field: 'end', headerName: 'End Date' },
    { field: 'weeklyHours', headerName: 'Weekly Hours' },
    { field: 'baseHourlyRate', headerName: 'Base Rate' },
    { field: 'presentHourlyRate', headerName: 'Present Rate' },
    { field: 'futureHourlyRate', headerName: 'Future Rate' },
    { field: 'presentValue', headerName: 'Present Value' },
    { field: 'futureValue', headerName: 'Future Value' },
];

// Columns for displaying user-inputted income sources
const userInputIncomeSourcesColumns = [
    { field: 'description', headerName: 'Description' },
    { field: 'displayAmount', headerName: 'Amount', align: 'right' },
    { field: 'displayPayInterval', headerName: 'Pay Interval' },
    { field: 'displayStartDate', headerName: 'Start Date' },
    { field: 'endType', headerName: 'End Type' },
    { field: 'displayEndDateDuration', headerName: 'End Date/Duration' },
    { field: 'displayLostCapacity', headerName: 'Lost Capacity (%)', align: 'right' },
];

// --- End of Helper functions ---

const ReviewStep = () => {
    const { reportData } = useReport();

    // Memoize formattedIncomeSources. This must be done before any conditional returns.
    // reportData might be null initially, so guard access to its properties.
    const formattedIncomeSources = React.useMemo(() => {
        if (!reportData?.incomePeriods) return [];
        return reportData.incomePeriods.map(source => ({
            ...source,
            id: source.trackingKey, // for table key
            displayAmount: formatValue(source.amount, 'amount'),
            displayPayInterval: source.payInterval ? source.payInterval.replace(/([A-Z])/g, ' $1').trim() : 'N/A',
            displayStartDate: formatValue(source.startDate),
            displayEndDateDuration: source.endType === 'EndDate'
                ? formatValue(source.endDate)
                : `${source.duration || 'N/A'} ${source.durationUnit || ''}`.trim(),
            displayLostCapacity: typeof source.lostEarningCapacity === 'number' ? `${(source.lostEarningCapacity * 100).toFixed(0)}%` : 'N/A',
        }));
    }, [reportData?.incomePeriods]);

    // Ensure reportData exists before trying to access its properties for rendering
    if (!reportData) return <Typography>Loading data...</Typography>;

    // Extract data for each step for clarity
    // This can now safely access reportData properties
    const step1Data = {
        claimantName: reportData.fullName, claimantDOB: reportData.dateOfBirth, claimantGender: reportData.gender, claimantEducation: reportData.educationLevel, 
        claimantEmploymentStatus: reportData.employmentStatus, claimantState: reportData.provinceOrState,
    };
    const step2Data = {
        valuationDate: reportData.valuationDate, lifeExpectancyYears: reportData.lifeExpectancyYears, expectedEndOfLife: reportData.expectedEndOfLife, worklifeExpectancyYears: reportData.worklifeExpectancyYears, retirementAge: reportData.retirementAge, discountRate: reportData.discountRate, growthRate: reportData.growthRate, netDiscountRate: reportData.netDiscountRate,
    };
    const step5HouseholdFactor = {
        regionalAdjustmentFactor: reportData.householdServices?.regionalAdjustmentFactor // Keep optional chaining as householdServices might be undefined
    };

    return (
        <Paper elevation={1} sx={{ p: 3, mt: 3, borderRadius: 2 }}> {/* Increased padding, margin-top, added elevation and border radius */}
            <Typography variant="h4" gutterBottom align="center" sx={{ mb: 2, fontWeight: 'medium' }}> {/* Changed variant to h4, adjusted margin */}
                Review Inputs
            </Typography>
            <Divider sx={{ mb: 4, mt: 1 }} /> {/* Increased margins for main divider */}

            {/* Render sections */}
            {renderSimpleFields(step1Data, 'Claimant Information')}
            <Divider sx={{ my: 3 }} /> {/* Increased vertical margin */}

            {/* Custom rendering for Dates & Rates section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>Dates & Rates</Typography>
                {/* Row 1: Valuation Date */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        {renderFieldWithValue('valuationDate', reportData.valuationDate)}
                    </Grid>
                </Grid>

                {/* Row 2: Worklife Expectancy and Retirement Age Group */}
                <Grid container spacing={2} sx={{ mt: 1 }}> {/* Added small top margin */}
                    <Grid item xs={12} md={6}>
                        {renderFieldWithValue('worklifeExpectancyYears', reportData.worklifeExpectancyYears)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderFieldWithValue('retirementAge', reportData.retirementAge)}
                    </Grid>
                </Grid>

                {/* Row 3: Life Expectancy and Projected Lifespan (Age) Group */}
                <Grid container spacing={2} sx={{ mt: 1 }}> {/* Added small top margin */}
                    <Grid item xs={12} md={6}>
                        {renderFieldWithValue('lifeExpectancyYears', reportData.lifeExpectancyYears)}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {renderFieldWithValue('Projected Lifespan (Age)', reportData.lifespanAge)}
                    </Grid>
                </Grid>

                {/* Row 4: Rates Group */}
                <Grid container spacing={2} sx={{ mt: 1 }}> {/* Added small top margin */}
                    <Grid item xs={12} sm={4}>
                        {renderFieldWithValue('discountRate', reportData.discountRate, 'discountRate')}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {renderFieldWithValue('growthRate', reportData.growthRate, 'growthRate')}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        {renderFieldWithValue('netDiscountRate', reportData.netDiscountRate, 'netDiscountRate')}
                    </Grid>
                </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Display User Inputted Income Sources */}
            {formattedIncomeSources.length > 0 && (
                renderTable(
                    formattedIncomeSources,
                    'Income Sources (Input)',
                    userInputIncomeSourcesColumns
                )
            )}
            
            {/* {reportData.householdServices && ( */}
            {false && reportData.householdServices && ( /* Keeping this logic as is */
                <>
                    <Divider sx={{ my: 3 }} /> {/* Increased vertical margin */}
                    <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>Household Services</Typography>
                    {renderSimpleFields(step5HouseholdFactor)}
                    {renderTable(reportData.householdServices?.periods, 'Household Service Periods', householdServicesColumns)}
                </>
            )}

            {/* {reportData.lifeCarePlan && ( */}
            {reportData.lifeCarePlan && ( /* Keeping this logic as is */
                <>
                    <Divider sx={{ my: 3 }} /> {/* Increased vertical margin */}
                    <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>Life Care Plan</Typography>
                    {Object.entries(lifeCarePlanCategoryConfig).map(([category, config]) =>
                        renderTable(
                            reportData.lifeCarePlan?.[category],
                            config.title,
                            config.columns.map(c => ({ ...c, headerName: c.reviewHeader || c.headerName }))
                        )
                    )}
                </>
            )}
        </Paper>
    );
}

export default ReviewStep;